import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

// import gsap from 'gsap'
// import { useGSAP } from '@gsap/react'
// import { ScrollTrigger } from 'gsap/ScrollTrigger'
import CTAButton from 'src/components/common/CTAButtonNew'
import { useSelector } from 'react-redux'
import { selectCTAParams } from 'src/modules/reducers/params'
import { defaultCTAURI } from 'src/modules/utils/constants'
import { createUrl } from 'src/modules/utils/createUrl'
import LazyImage from '../LazyLoad/LazyImage'
import { replaceUrlParam } from 'src/modules/utils/replaceUrlParam'
import { useCtaText } from 'src/modules/hooks/useCtaText'
import useIsMobile from 'src/modules/hooks/useIsMobile'
import {
  colors,
  spacing,
  mediaQuery,
  heading,
  typographyMixin,
  body,
} from 'src/constants/marketing-ds'
import { fireUIInteractionEvent } from 'src/modules/analytics/v3'
import { selectStateForProtobuf } from 'src/modules/reducers'
import { SectionContainer, SectionHeader } from 'src/components/common/SharedComponents'

const PlatformSection = ({
  ctaText,
  ctaHrefOverride,
  lang = 'en',
  market,
  hasOfferInput,
  platformImage,
  platformHeadline,
  disableFreeTrial,
  platformFootnote,
  excludedDevices = [],
  isCTADisabled,
}) => {
  // get ctaParams from redux
  const parsedParams = useSelector(selectCTAParams)
  let ctaURL = createUrl(defaultCTAURI, parsedParams)
  const stateForProtobuf = useSelector(selectStateForProtobuf)

  const isMobile = useIsMobile(768)

  const logosUrlPrefix = 'https://assets-imgx.fubo.tv/marketing/lp/images/platforms/2024/'
  const logosUrlSuffix = '?ch=width&auto=format,compress'

  const platformLogosDesktop = [
    {
      src: 'roku.png',
      alt: 'Roku',
    },
    {
      src: 'firetv.png',
      alt: 'Fire TV',
    },
    {
      src: 'lg.png',
      alt: 'LG',
    },
    {
      src: 'vizio.png',
      alt: 'Vizio',
    },
    {
      src: 'appletv.png',
      alt: 'Apple TV',
    },
    {
      src: 'hisense.png',
      alt: 'Hisense',
    },
    {
      src: 'android.png',
      alt: 'Android',
    },
    {
      src: 'ios.png',
      alt: 'iOS',
    },
    {
      src: 'xbox-w-2.png',
      alt: 'Xbox',
    },
    {
      alt: lang === 'es' ? 'Y MÁS' : '+ MORE',
    },
  ]

  const platformLogosMobile = [
    {
      src: 'roku.png',
      alt: 'Roku',
    },
    {
      src: 'firetv.png',
      alt: 'Fire TV',
    },
    {
      src: 'lg.png',
      alt: 'LG',
    },
    {
      src: 'vizio.png',
      alt: 'Vizio',
    },
    {
      src: 'appletv.png',
      alt: 'Apple TV',
    },
    {
      src: 'hisense.png',
      alt: 'Hisense',
    },
    {
      src: 'android.png',
      alt: 'Android',
    },
    {
      src: 'ios.png',
      alt: 'iOS',
    },
    {
      alt: lang === 'es' ? 'Y MÁS' : '+ MORE',
    },
    {
      display: false,
    },
  ]

  const [platformLogos, setPlatformLogos] = useState(platformLogosDesktop)

  useEffect(() => {
    if (isMobile) {
      setPlatformLogos(platformLogosMobile)
    } else {
      setPlatformLogos(platformLogosDesktop)
    }
  }, [isMobile])

  const ctaCopy = useCtaText({ lang, market, disableFreeTrial, ctaText })
  ctaURL = replaceUrlParam(ctaURL, 'comp', 'platform')
  const platformMarket = {
    all: 'https://assets-imgx.fubo.tv/marketing/lp/images/devices/devices-nfl.png',
    us: 'https://assets-imgx.fubo.tv/marketing/lp/images/devices/devices-nfl.png',
    latino: 'https://assets-imgx.fubo.tv/marketing/lp/images/posters/latino-platform-poster.png',
    canada: 'https://assets-imgx.fubo.tv/marketing/lp/images/devices/ca-devices-2025.png',
    spain: 'https://assets-imgx.fubo.tv/marketing/lp/images/posters/latino-platform-poster.png',
  }

  const platformImg = platformImage || platformMarket[market || 'all']

  const platformHeader =
    platformHeadline ||
    (lang === 'es' ? (
      'Mira TV en vivo y contenido bajo demanda desde cualquier lugar'
    ) : (
      <>
        Stream live and<span className="mobile-line-break"> on demand from anywhere</span>
      </>
    ))

  const handleOnClick = () => {
    fireUIInteractionEvent(
      {
        element: ctaCopy,
        component: 'platform_section_cta_clicked',
        type: 'clicked',
      },
      stateForProtobuf
    )
  }

  return (
    <SectionContainer backgroundColor={colors.gradients.slate.light}>
      <StyledPlatformSection>
        <SectionHeader title={platformHeader} variant="light" />
        <div className="platform-content">
          <div className="platform-image">
            <LazyImage src={platformImg} alt="" />
          </div>
          <div className="platform-logos">
            {platformLogos
              .filter((logo) => !excludedDevices.includes(logo.alt))
              .map((logo, index) => {
                const url = logosUrlPrefix + logo.src + logosUrlSuffix
                const alt = logo.alt
                return logo.src ? (
                  <div className="platform-logo-item" key={index}>
                    <LazyImage src={url} alt={alt} />
                  </div>
                ) : logo.alt ? (
                  <div className="platform-logo-item" key={index}>
                    <span className="platform-logo-text">{alt}</span>
                  </div>
                ) : (
                  <div className="platform-logo-item" style={{ display: 'none' }} key={index}></div>
                )
              })}
          </div>
        </div>
        <div className="cta-container">
          <CTAButton
            isOutlined={true}
            isDisabled={isCTADisabled}
            link={hasOfferInput ? '#' : ctaHrefOverride || ctaURL}
            onClick={handleOnClick}
          >
            {ctaCopy}
          </CTAButton>
          {platformFootnote && <div className="platform-footnote"> {platformFootnote}</div>}
        </div>
      </StyledPlatformSection>
    </SectionContainer>
  )
}

export default PlatformSection

const StyledPlatformSection = styled.section`
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  overflow: hidden;
  padding: ${spacing.xl} 0;
  gap: ${spacing.xl};

  ${mediaQuery.tablet} {
    padding: ${spacing.xxxl} 0;
    gap: ${spacing.xxl};
  }

  .platform-content {
    max-width: 100%;

    .platform-image {
      max-width: 680px;
      margin: 0 auto ${spacing.md};

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    .platform-logos {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      grid-template-rows: repeat(3, 1fr);
      gap: ${spacing.lg};
      margin: 0 auto;
      max-width: 450px;
      place-items: center;

      ${mediaQuery.tablet} {
        max-width: 800px;
        grid-template-columns: repeat(5, 1fr);
        grid-template-rows: repeat(2, 1fr);
      }

      .platform-logo-item {
        text-align: center;
        display: flex;
        justify-content: center;
        align-items: center;
        max-height: 62px;
        max-width: 80px;
        height: 62px;
        width: auto;
        position: relative;
        color: ${colors.white};

        ${typographyMixin(heading.sm)}
        ${mediaQuery.tablet} {
          max-height: 62px;
          max-width: 130px;
          height: 62px;
          width: auto;
        }
        ${mediaQuery.desktop} {
          ${typographyMixin(heading.md)}
        }

        img {
          width: 95%;
          height: 95%;

          ${mediaQuery.tablet} {
            width: 80%;
            height: 80%;
          }

          object-fit: contain;
          filter: brightness(0) invert(1); // Make logos white
        }
      }
    }
  }

  .platform-footnote {
    color: ${colors.white};
    text-align: center;
    margin-top: ${spacing.md};
    ${typographyMixin(body.md)}
  }
`
